import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { GitAuthorizedEnum } from '@mogenius/git/enums/git-authorized.enum';
import { GitlabAuthorizedResponseDto } from '@mogenius/client-sdk/core-dto';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { MoUtils } from '@mogenius/client-sdk/utils';
import { environment } from '@mogenius/environments/environment';
import { map } from 'rxjs/operators';
import { plainToInstance } from 'class-transformer';

import { NgOptimizedImage } from '@angular/common';
import { SpinnerComponent } from '@mogenius/shared/components/spinner/spinner.component';

@Component({
  selector: 'mo-bitbucket-authorized-page',
  templateUrl: './bitbucket-authorized-page.component.html',
  styleUrl: './bitbucket-authorized-page.component.scss',
  imports: [NgOptimizedImage, SpinnerComponent]
})
export class BitbucketAuthorizedPageComponent implements OnInit, OnDestroy {
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _httpClient = inject(HttpClient);
  private readonly _router = inject(Router);

  private _subscriptions: Subscription;

  private _state: GitAuthorizedEnum = GitAuthorizedEnum.LOADING;
  private _data: GitlabAuthorizedResponseDto;
  private _message: string;
  private _fallbackUrl = localStorage.getItem('gitConnectionFallbackUrl') || undefined;

  ngOnInit(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
    this._subscriptions = new Subscription();
    this._subscriptions.add(
      this._activatedRoute.queryParams.subscribe({
        next: async (params: Params): Promise<void> => {
          this.bitbucketAuthorized(params);
          await this._router.navigate([], {
            queryParams: {
              state: null,
              code: null,
              installation_id: null
            },
            queryParamsHandling: 'merge'
          });
        },
        error: () => {},
        complete: () => {}
      })
    );
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  private bitbucketAuthorized(params: { [key: string]: string }): void {
    if (!params?.state || Object.keys(params).length === 0) {
      setTimeout(() => {
        if (this._state === GitAuthorizedEnum.LOADING) {
          this._state = GitAuthorizedEnum.FAILED;
        }
      }, 5000);
      return;
    }
    const url = MoUtils.cleanUpUrl(`${this.serviceUrl}/${environment.bitbucketService.bitbucketAuthorized.endPoint}`);
    this._state = GitAuthorizedEnum.LOADING;

    this._subscriptions.add(
      this._httpClient
        .get<GitlabAuthorizedResponseDto>(url, {
          headers: {
            'Content-Type': environment.bitbucketService.bitbucketAuthorized.header.contentType,
            hideLoadingSpinner: '1',
            hideErrorMessage: '1'
          },
          params: params
        })
        .pipe(
          map((response: GitlabAuthorizedResponseDto) =>
            plainToInstance(GitlabAuthorizedResponseDto, response, { excludeExtraneousValues: true })
          )
        )
        .subscribe({
          next: async (data: GitlabAuthorizedResponseDto): Promise<void> => {
            this._state = GitAuthorizedEnum.SUCCESS;
            this._data = data;
            if (this._data.type === 'AUTHORIZED' && (this._data.callbackUrl || this._fallbackUrl)) {
              window.open(this._fallbackUrl ?? this._data.callbackUrl, '_self');
            }
          },
          error: (err) => {
            this._state = GitAuthorizedEnum.FAILED;
            if (err.status === HttpStatusCode.Unauthorized) {
              console.log(err);
              this._data = {
                callbackUrl: err?.error?.callbackUrl
              } as any;
              this._message = err?.error?.message;
            }
          },
          complete: () => {}
        })
    );
  }

  private get serviceUrl(): string {
    if (environment.bitbucketService.port) {
      return MoUtils.cleanUpUrl(`${environment.bitbucketService.url}:${environment.bitbucketService.port}/`);
    }
    return MoUtils.cleanUpUrl(`${environment.bitbucketService.url}/`);
  }

  get state(): GitAuthorizedEnum {
    return this._state;
  }

  get gitAuthorizedEnum(): typeof GitAuthorizedEnum {
    return GitAuthorizedEnum;
  }

  get data(): GitlabAuthorizedResponseDto {
    return this._data;
  }

  get message(): string {
    return this._message;
  }

  get fallbackUrl(): string | undefined {
    return this._fallbackUrl;
  }
}
